import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /**
   * Option groups the shopper has explicitly confirmed (click, Space, or a
   * real radio `change`), keyed by normalized option name and holding the
   * option-value id that was confirmed. A value id, not a boolean, so a
   * server-driven change to what's actually selected (e.g. an auto-resolved
   * combination after morph) can be told apart from the shopper re-affirming
   * the same choice. See DESIGN-DECISIONS.md "State contract".
   * @type {Map<string, string | undefined>}
   */
  confirmedOptions = new Map();

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    /* GW (round 3 — colorway variants): the same custom element now drives TWO
       markups. Horizon's is `<label><input type=radio>`, which fires `change`.
       GW-OPTION-SELECT (blocks/gw-color-picker.liquid) is real
       `<button aria-pressed>` controls, because 02-COMPONENTS §7.4 and
       04-GUARDRAILS §5.3 both name a styled radio / label hack as the thing the
       component must NOT be. Buttons fire `click`, so the click is translated
       into the one code path that already exists — variantChanged() only ever
       reads `event.target`. Nothing about the radio path changes. */
    this.addEventListener('click', this.#optionButtonClicked);
    this.addEventListener('keydown', (event) => {
      // Space does not click a native radio that is already checked.
      if (event.key === ' ' && !event.repeat && event.target instanceof HTMLInputElement) {
        this.#optionButtonClicked(event);
      }
    });

    this.#resizeObserver.observe(this);
    // First paint: confirmedOptions is empty, so every group reports pending.
    this.#syncConfirmedState();
  }

  /**
   * Canonicalizes a raw `data-option-name` into the key `confirmedOptions`
   * is keyed by: lowercase, parenthesized suffix extracted (unchanged from
   * before), then any name containing "color"/"colour" - including a live
   * Shopify option literally named "Colorway"/"Colourway", not just
   * "Color"/"Colour" - collapses to "color", and any name containing "size"
   * collapses to "size". This must match the Liquid contains-color /
   * contains-size triggers in blocks/buy-buttons.liquid
   * (gw_confirm_color/gw_confirm_size) and
   * snippets/variant-main-picker.liquid (gw_confirm_choice), since
   * product-form.js's gate checks confirmedOptions.has('color') /
   * .has('size') literally. Defined once and called from both
   * variantChanged and #syncConfirmedState so the two cannot drift apart.
   * Other names pass through unchanged.
   * @param {string} rawOptionName
   * @returns {string}
   */
  #canonicalOptionKey(rawOptionName) {
    const key = rawOptionName.toLowerCase().replace(/^.*\(([^)]+)\)$/, '$1');
    if (key.includes('color') || key.includes('colour')) return 'color';
    if (key.includes('size')) return 'size';
    return key;
  }

  /** All selectable groups, using the same value identity as the visible preview. */
  get choiceConfirmation() {
    const groups = [...(this.refs.fieldsets || []), ...this.querySelectorAll('.variant-option--dropdowns')];
    return groups.flatMap((group) => {
      const controls = /** @type {NodeListOf<HTMLElement>} */ (group.querySelectorAll('input[type="radio"][data-option-value-id], button[data-option-value-id], select option[data-option-value-id]'));
      if (controls.length < 2) return [];
      const selected = /** @type {HTMLElement | null} */ (group.querySelector('input:checked, button[aria-pressed="true"], select option:checked'));
      const label = selected?.dataset.optionName || controls[0]?.dataset.optionName || '';
      const key = this.#canonicalOptionKey(label);
      const valueId = selected?.dataset.optionValueId;
      return [{
        label,
        value: selected instanceof HTMLInputElement ? selected.value : selected?.textContent?.trim() || '',
        confirmed: valueId !== undefined && this.confirmedOptions.has(key) && this.confirmedOptions.get(key) === valueId,
      }];
    });
  }

  /**
   * Translates a click on a GW-OPTION-SELECT button into the shared variant-change path.
   * @param {Event} event - The click event.
   */
  #optionButtonClicked = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (
      target instanceof HTMLInputElement &&
      target.type === 'radio' &&
      target.dataset.currentChecked === 'true' &&
      this.contains(target) &&
      (this.closest('dialog') || this.closest('.shopify-section') || this.closest('main'))?.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] [data-gw-selection-required], product-form-component[data-product-id="${this.dataset.productId}"][data-gw-options-pending="true"]`
      )
    ) {
      if (event.type === 'keydown') event.preventDefault();
      this.variantChanged(event);
      return;
    }

    const button = target.closest('button[data-option-value-id]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    if (button.getAttribute('aria-pressed') === 'true') {
      if (this.dataset.gwDeselectable === 'true') {
        this.#deselectOptionButtons(button);
      }
      return;
    }

    this.variantChanged(/** @type {Event} */ (/** @type {unknown} */ ({ target: button })));
  };

  /**
   * Returns a GW product picker to its server-rendered unselected state.
   * The request deliberately carries neither `variant` nor `option_values`, so
   * Liquid sees `product.selected_variant == nil` and renders the full gallery.
   * @param {HTMLButtonElement} button - The currently pressed option button.
   */
  #deselectOptionButtons(button) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !button.closest('product-card') &&
      !button.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    for (const option of this.querySelectorAll('button[data-option-value-id]')) {
      option.setAttribute('aria-pressed', 'false');
    }
    for (const readout of this.querySelectorAll('[data-key^="gw-optsel-value-"]')) {
      readout.textContent = 'Select one';
    }

    this.fetchUpdatedSection(this.buildUnselectedRequestUrl(), {
      detail: { optionValueId: '', variantId: '', connectedProductUrl: '', selectionCleared: true },
    });

    this.confirmedOptions.clear();
    this.#syncConfirmedState();
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    const optionName = selectedOption.dataset.optionName || event.target.dataset.optionName;

    /* GW: quick-add dispatches a synthetic `change` purely to carry the
       product card's already-selected swatch into a freshly opened modal
       (quick-add.js #syncVariantSelection). That sync is not the shopper
       confirming anything in the modal, so it must update the visible
       selection without marking the group confirmed — DESIGN-BRIEF: "quick
       add does a second morph and synthetic change while opening is not
       shopper confirmation". */
    if (optionName && !(/** @type {{gwSync?: boolean}} */ (event).gwSync)) {
      const key = this.#canonicalOptionKey(optionName);
      this.confirmedOptions.set(key, selectedOption.dataset.optionValueId);
    }

    if (
      !(event.target instanceof HTMLInputElement &&
        event.target.type === 'radio' &&
        event.target.dataset.currentChecked === 'true')
    ) {
      this.updateSelectedOption(event.target);
    }

    this.#syncConfirmedState();

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Projects confirmedOptions onto the rendered fieldsets so CSS can show a
   * neutral "pending" presentation until a group is explicitly confirmed, and
   * a "confirmed" presentation once it is - per group, not per picker. A
   * group reads as confirmed only when confirmedOptions.has(key) is true AND
   * its recorded value id matches the value actually selected right now (not
   * merely a coincidental match against an absent/undefined value id) - if
   * the server renders a different value under a previously-confirmed group
   * (e.g. an auto-resolved combination after morph), the confirmation is
   * dropped rather than shown as a false positive. Also keeps each group's
   * accessible status text (data-gw-group-status) and the picker-level
   * preview note (data-gw-preview-note) in step. No-ops for pickers with no
   * fieldsets (dropdown style), and harmless for pickers that never opt into
   * the pending/confirmed presentation via data-gw-confirm-choice.
   */
  #syncConfirmedState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const isConfirmChoicePicker = this.dataset.gwConfirmChoice === 'true';
    let allGroupsConfirmed = fieldsets.length > 0;

    for (const fieldset of fieldsets) {
      const selected = fieldset.querySelector('input:checked, button[aria-pressed="true"]');
      if (!(selected instanceof HTMLElement)) {
        allGroupsConfirmed = false;
        continue;
      }

      const rawName = selected.dataset.optionName;
      if (!rawName) continue;

      const key = this.#canonicalOptionKey(rawName);
      const currentValueId = selected.dataset.optionValueId;

      if (this.confirmedOptions.has(key) && this.confirmedOptions.get(key) !== currentValueId) {
        this.confirmedOptions.delete(key);
      }

      const isConfirmed = this.confirmedOptions.has(key) && this.confirmedOptions.get(key) === currentValueId;
      fieldset.dataset.gwConfirmed = String(isConfirmed);
      if (!isConfirmed) allGroupsConfirmed = false;

      const statusEl = fieldset.querySelector('[data-gw-group-status]');
      if (statusEl) {
        const value = selected instanceof HTMLInputElement ? selected.value : selected.textContent?.trim() || '';
        const prefix = isConfirmed
          ? this.dataset.gwGroupConfirmedPrefix || 'Confirmed: '
          : this.dataset.gwGroupPendingPrefix || 'Previewing: ';
        statusEl.textContent = prefix + value;
      }
    }

    if (isConfirmChoicePicker) {
      const previewNote = this.querySelector('[data-gw-preview-note]');
      if (previewNote instanceof HTMLElement) {
        const groups = this.choiceConfirmation;
        previewNote.hidden = groups.length ? groups.every((group) => group.confirmed) : allGroupsConfirmed;
      }
    }
  }

  /**
   * Clears all explicit shopper confirmations and re-projects pending state
   * onto the DOM. For callers managing a persistent, reused picker instance
   * (quick-add.js's singleton modal picker) where a fresh or reopened
   * chooser must not silently carry over a confirmation from a previous
   * open - this session or a prior one of the same cached product.
   */
  resetConfirmedState() {
    this.confirmedOptions.clear();
    this.#syncConfirmedState();
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    /* GW: the button rendering. Selection lives in aria-pressed, which is both
       the state carrier and the a11y contract (02-COMPONENTS §7.4). Scope the
       reset to the option's own <fieldset> so a product with two options does
       not deselect the other one. */
    if (target instanceof HTMLButtonElement) {
      const group = target.closest('fieldset') ?? this;

      for (const option of group.querySelectorAll('button[data-option-value-id]')) {
        option.setAttribute('aria-pressed', option === target ? 'true' : 'false');
      }

      // Keep the visible "Color: Tan" readout in step immediately; the morph
      // that follows the fetch confirms it from the server render.
      const position = target.dataset.optionPosition;
      const readout = position && group.querySelector(`[data-key="gw-optsel-value-${position}"]`);
      const label = target.querySelector('.gw-optsel__label');
      if (readout && label) readout.textContent = label.textContent?.trim() ?? '';
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Builds the base product URL used when no variant is selected.
   * Alternative `view` templates remain intact, but variant-selection params do not.
   * @returns {string}
   */
  buildUnselectedRequestUrl() {
    const productUrl = this.dataset.productUrl?.split('?')[0] || window.location.pathname;
    const url = new URL(productUrl, window.location.origin);
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) url.searchParams.set('view', viewParamValue);
    return url.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, selectionCleared?: boolean }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '', selectionCleared = false } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          selectionCleared,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => { if (!response.ok) throw new Error("Selection could not be updated"); return response.text(); })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
              selectionCleared,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
                selectionCleared,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
            selectionCleared,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    const incomingRadios = [.../** @type {NodeListOf<HTMLInputElement>} */ (newVariantPickerSource.querySelectorAll('input[type="radio"]'))];
    const checkedValues = new Set(
      incomingRadios.filter((radio) => radio.hasAttribute('checked')).map((radio) => radio.dataset.optionValueId)
    );

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    // Morph can preserve a radio's dirty checked property and this element's
    // old input references across products. Reconcile both with the incoming
    // server selection, without treating it as shopper confirmation.
    if (incomingRadios.length) {
      this.#radios = (this.refs.fieldsets || []).map((fieldset) => [.../** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input[type="radio"]'))]);
      this.#checkedIndices = this.#radios.map((radios) => {
        for (const radio of radios) {
          const checked = checkedValues.has(radio.dataset.optionValueId);
          radio.checked = checked;
          radio.dataset.currentChecked = String(checked);
          radio.dataset.previousChecked = 'false';
        }
        const index = radios.findIndex((radio) => radio.checked);
        return index < 0 ? [] : [index];
      });
    }
    this.updateVariantPickerCss();
    // Re-apply pending/confirmed state to the freshly morphed fieldsets - the
    // server-rendered markup carries no opinion on it. Also covers quick-add's
    // second morph (quick-add.js calls this directly after its own morph).
    this.#syncConfirmedState();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * The one selector that matches a selected option in every rendering this
   * component supports: Horizon's dropdown, Horizon's radio, and GW's button.
   * A given picker instance only ever uses one of them, so document-order
   * ambiguity (HANDOFF gotcha 11) cannot bite here.
   * @type {string}
   */
  static SELECTED_OPTION_SELECTOR =
    'select option[selected], fieldset input:checked, button[data-option-value-id][aria-pressed="true"]';

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | HTMLButtonElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(VariantPicker.SELECTED_OPTION_SELECTOR);

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement ||
        selectedOption instanceof HTMLButtonElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll(VariantPicker.SELECTED_OPTION_SELECTOR));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
